import React, { Fragment, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { css, useTheme } from 'styled-components'
import { forgotPassword } from '../../actions/auth'
import { postToLoggingServiceNotAuthenticated } from '../../actions/logging'
import { isValidEmailAddress } from '../../utils/string'
import { H3, P2, ErrorTypography, Input } from '../styled/typography/typography'

const ForgotPasswordForm = styled.form`
  padding: 0;
  margin-top: 25px;

  ${props =>
    props.error &&
    css`
      p {
        margin-bottom: 4px;
      }
    `}
`

const HelpTextParagraph = styled(P2).attrs({
  as: 'p'
})`
  padding: 0 10px;
  margin-bottom: 30px;
  text-align: center;
`

const ReturnToLogin = styled.button`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 14px;
`

export const ForgotPassword = ({ dispatch, onReturnClick }) => {
  const theme = useTheme()
  const [resetPasswordEmail, setResetPasswordEmail] = useState('')
  const [displayValidationError, setDisplayValidationError] = useState(false)
  const [successfullySent, setSuccessfullySent] = useState(false)

  const resetPasswordEmailRef = useRef(null)
  const forgotPasswordHelpRef = useRef(null)

  const checkForSubmit = async e => {
    if (e.which === 13 || e.keyCode === 13) {
      await onResetMyPasswordClick(e)
    }
  }

  const getHeaderText = () => {
    return !successfullySent ? 'Forgot Password' : 'Please check your email!'
  }

  const onHideForgotPasswordClick = () => {
    setResetPasswordEmail('')
    onReturnClick()
  }

  const handleResetMyPasswordChange = e => {
    setResetPasswordEmail(e.target.value)
    setDisplayValidationError(false)
  }

  const shouldForgotPasswordButtonBeDisabled = () => {
    return !resetPasswordEmail || displayValidationError
  }

  const onResetMyPasswordClick = async e => {
    if (isValidEmailAddress(resetPasswordEmail)) {
      e.preventDefault()
      await dispatch(forgotPassword(resetPasswordEmail))

      resetPasswordEmailRef.current.value = ''
      forgotPasswordHelpRef.current.innerText =
        'A reset password link has been sent. Please check your email.'

      dispatch(
        postToLoggingServiceNotAuthenticated('forgot_password', {
          action: 'email_password_click',
          email: resetPasswordEmail
        })
      )

      setSuccessfullySent(true)
      setResetPasswordEmail('')
    } else {
      setDisplayValidationError(true)
    }
  }

  return (
    <Fragment>
      <div className="welcome-back">
        <H3 as="h3">{getHeaderText()}</H3>
      </div>
      {!successfullySent ? (
        <ForgotPasswordForm
          error={displayValidationError}
          className="forgot-password"
          action="javascript:void(0);"
        >
          <HelpTextParagraph
            ref={forgotPasswordHelpRef}
            className="forgot-password-help"
          >
            Don't worry, it happens to the best of us. Enter your email to
            receive a reset password link.
          </HelpTextParagraph>
          {displayValidationError && (
            <ErrorTypography
              className="status-text"
              style={{ margin: '15px 0' }}
            >
              Please enter a valid email address.
            </ErrorTypography>
          )}
          <div className="form-row lst">
            <Input
              type="email"
              ref={resetPasswordEmailRef}
              id="reset-password-email-input"
              autoFocus
              onChange={handleResetMyPasswordChange}
              onKeyPress={
                !shouldForgotPasswordButtonBeDisabled() ? checkForSubmit : null
              }
              placeholder="Email Address"
            />
          </div>
          <button
            className={`quest-btn btn-a btn-a-${
              theme?.style
            } reset-my-password ${
              shouldForgotPasswordButtonBeDisabled() ? 'disabled' : 'active'
            }`}
            onClick={
              !shouldForgotPasswordButtonBeDisabled()
                ? onResetMyPasswordClick
                : null
            }
          >
            Email Password Link
          </button>
        </ForgotPasswordForm>
      ) : (
        <div>
          <P2
            as="p"
            ref={forgotPasswordHelpRef}
            className="forgot-password-help sent"
          >
            We sent you a secret link to reset your password.
            <br />
            <br />
            If the email does not arrive soon, check your spam folder. It was
            sent from <b>help@questforward.org</b>
          </P2>
        </div>
      )}
      <ReturnToLogin
        className="quest-text-btn-dark"
        id="forgetYourPassword"
        onClick={onHideForgotPasswordClick}
      >
        Return to Login
      </ReturnToLogin>
    </Fragment>
  )
}

ForgotPassword.propTypes = {
  dispatch: PropTypes.func,
  onReturnClick: PropTypes.func
}

export default ForgotPassword
